import { useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import * as _m from "~/paraglide/messages";
import { trackEvent } from "~/lib/posthog-client";
import { client } from "~/lib/orpc-client";
import {
  ANNEX_III_AUDIT_EVENTS,
  logAnnexIIIAuditEvent,
} from "~/lib/annex-iii-audit";

// Tolerant message accessor: a not-yet-compiled paraglide key returns the
// fallback instead of throwing, so the shared shell never crashes on a missing key.
const m = _m as unknown as Record<string, ((...args: unknown[]) => string) | undefined>;

function msg(key: string, fallback: string, params?: Record<string, string>): string {
  try {
    const fn = m[key];
    return typeof fn === "function" ? fn(params ?? {}) : fallback;
  } catch {
    return fallback;
  }
}

// Session-scoped dismiss flag (mirrors LowCreditBanner's DISMISS_KEY convention).
const DISMISS_KEY = "candidateAiNoticeDismissed";
// Legacy local cache for offline/unauthenticated fallback; server persistence is authoritative when available.
const HUMAN_REVIEW_KEY = "alchema.humanReviewPreference";
const HUMAN_REVIEW_BACKFILLED_KEY = "alchema.humanReviewPreference.serverBackfilled";

function readLocalHumanReviewPreference(): boolean | null {
  if (typeof window === "undefined") return null;
  try {
    const value = localStorage.getItem(HUMAN_REVIEW_KEY);
    if (value === "true") return true;
    if (value === "false") return false;
  } catch {
    // Preserve silent local fallback behavior on storage errors.
  }
  return null;
}

function writeLocalHumanReviewPreference(value: boolean): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(HUMAN_REVIEW_KEY, value ? "true" : "false");
  } catch {
    // Preserve silent local fallback behavior on storage errors.
  }
}

function hasBackfilledLocalPreference(): boolean {
  if (typeof window === "undefined") return true;
  try {
    return localStorage.getItem(HUMAN_REVIEW_BACKFILLED_KEY) === "true";
  } catch {
    return true;
  }
}

function markLocalPreferenceBackfilled(): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(HUMAN_REVIEW_BACKFILLED_KEY, "true");
  } catch {
    // Best effort only; lack of marker must not affect the UI.
  }
}

/**
 * EU AI Act Article 50 candidate AI-interaction disclosure banner + human-review toggle.
 *
 * Renders a slim, accessible transparency notice on authenticated candidate
 * AI-interaction routes. Discloses that Alchema uses AI (Mistral) to produce
 * advisory suggestions the candidate reviews before use, links to the privacy
 * AI-processing section, and exposes an inline human-review preference toggle.
 *
 * Non-blocking: renders null safely on dismiss and guards all browser access
 * for SSR (mounts inside the shared authenticated shell).
 */
export function CandidateAiNotice() {
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      return sessionStorage.getItem(DISMISS_KEY) === "true";
    } catch {
      return false;
    }
  });

  const [humanReview, setHumanReview] = useState(() => readLocalHumanReviewPreference() ?? false);

  const disclosureLoggedRef = useRef(false);
  const shownTrackedRef = useRef(false);
  const userToggledRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    async function syncHumanReviewPreference() {
      const localPreference = readLocalHumanReviewPreference();
      try {
        const serverPreference = await client.candidatePreferences.getHumanReviewPreference();
        if (cancelled) return;

        if (serverPreference.hasServerPreference) {
          if (!userToggledRef.current) {
            setHumanReview(serverPreference.enabled);
            writeLocalHumanReviewPreference(serverPreference.enabled);
          }
          return;
        }

        if (localPreference !== null && !hasBackfilledLocalPreference()) {
          await client.candidatePreferences.setHumanReviewPreference({
            enabled: localPreference,
            source: "local_storage_backfill",
          });
          if (!cancelled) markLocalPreferenceBackfilled();
        }
      } catch {
        // Server sync/backfill is best-effort; localStorage remains the fallback.
      }
    }

    void syncHumanReviewPreference();

    return () => {
      cancelled = true;
    };
  }, []);

  // Fire the transparency-disclosure audit event + analytics exactly once on first view.
  useEffect(() => {
    if (dismissed) return;
    if (!disclosureLoggedRef.current) {
      disclosureLoggedRef.current = true;
      try {
        logAnnexIIIAuditEvent({
          eventType: ANNEX_III_AUDIT_EVENTS.TRANSPARENCY_DISCLOSURE_VIEWED,
          disclosuresConfirmed: true,
        });
      } catch {
        // Audit logging is best-effort; never block the UI.
      }
    }
    if (!shownTrackedRef.current) {
      shownTrackedRef.current = true;
      trackEvent("feature_used", { feature: "candidate_ai_notice", state: "shown" });
    }
  }, [dismissed]);

  if (dismissed) return null;

  function handleDismiss() {
    if (typeof window !== "undefined") {
      try {
        sessionStorage.setItem(DISMISS_KEY, "true");
      } catch {
        // Ignore storage errors (private mode / quota).
      }
    }
    setDismissed(true);
  }

  async function handleToggleHumanReview() {
    const next = !humanReview;
    userToggledRef.current = true;
    setHumanReview(next);
    writeLocalHumanReviewPreference(next);
    if (next) {
      try {
        logAnnexIIIAuditEvent({
          eventType: ANNEX_III_AUDIT_EVENTS.HUMAN_REVIEW_REQUESTED,
          isHumanReviewed: true,
          humanDecision: "pending",
        });
      } catch {
        // Best-effort audit.
      }
      trackEvent("feature_used", { feature: "candidate_ai_notice", state: "enabled" });
    } else {
      trackEvent("feature_used", { feature: "candidate_ai_notice", state: "disabled" });
    }

    try {
      await client.candidatePreferences.setHumanReviewPreference({
        enabled: next,
        source: "candidate_ai_notice",
      });
    } catch {
      // Server sync is best-effort; keep the local fallback value and avoid visible errors.
    }
  }

  return (
    <div
      role="region"
      aria-label={msg("candidate_ai_notice_aria", "AI transparency notice")}
      data-compliance="eu-ai-act-art-50"
      data-ai-provider="mistral"
      data-ai-disclosure="ai-interaction"
      className="flex flex-col gap-2 rounded-[10px] px-4 py-2.5 text-sm sm:flex-row sm:items-center sm:justify-between"
      style={{
        backgroundColor: "var(--surface-subtle, var(--warning-subtle))",
        color: "var(--text)",
        border: "1px solid oklch(0.7 0.05 260 / 0.2)",
      }}
    >
      <div className="flex flex-1 items-start gap-2.5">
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className="mt-0.5 flex-shrink-0"
          style={{ color: "var(--accent)" }}
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M12 16v-4" />
          <path d="M12 8h.01" />
        </svg>
        <p className="flex-1">
          {msg(
            "candidate_ai_notice_text",
            "Alchema uses AI (Mistral) to analyze, match, and draft your application materials. All outputs are advisory suggestions you review before use.",
          )}{" "}
          <Link
            to="/privacy"
            hash="ai-processing"
            className="font-medium underline transition-colors duration-200 hover:opacity-80"
            style={{ color: "var(--accent)" }}
            onClick={() => {
              trackEvent("feature_used", { feature: "candidate_ai_notice", state: "learn_more" });
            }}
          >
            {msg("candidate_ai_notice_learn_more", "Learn more")}
          </Link>
        </p>
      </div>

      <div className="flex shrink-0 flex-col gap-1 sm:items-end">
        <div className="flex items-center gap-2">
          <button
            type="button"
            role="switch"
            aria-checked={humanReview}
            onClick={handleToggleHumanReview}
            className="inline-flex items-center gap-2 rounded-[8px] px-1 py-0.5 text-xs font-medium transition-colors duration-200 focus:outline-none focus-visible:ring-2"
            style={{ color: "var(--text)" }}
          >
            <span
              aria-hidden="true"
              className="relative inline-flex h-4 w-7 items-center rounded-full transition-colors duration-200"
              style={{
                backgroundColor: humanReview ? "var(--accent)" : "var(--border, oklch(0.7 0.02 260 / 0.4))",
              }}
            >
              <span
                className="inline-block h-3 w-3 transform rounded-full bg-white transition-transform duration-200"
                style={{ transform: humanReview ? "translateX(14px)" : "translateX(2px)" }}
              />
            </span>
            <span>{msg("candidate_ai_notice_toggle_label", "Request human review of AI decisions")}</span>
          </button>

          <button
            type="button"
            onClick={handleDismiss}
            className="inline-flex items-center rounded-[8px] px-2 py-1 text-xs font-medium transition-colors duration-200 hover:opacity-70"
            style={{ color: "var(--text-muted)" }}
            aria-label={msg("candidate_ai_notice_dismiss", "Dismiss")}
          >
            {msg("candidate_ai_notice_dismiss", "Dismiss")}
          </button>
        </div>

        {humanReview && (
          <span
            className="inline-flex items-center gap-1 text-xs font-semibold"
            style={{ color: "var(--success, var(--accent))" }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M5 13l4 4L19 7" />
            </svg>
            {msg("candidate_ai_notice_toggle_on", "Human review requested")}
          </span>
        )}
        <span className="text-[11px]" style={{ color: "var(--text-muted)" }}>
          {msg(
            "candidate_ai_notice_toggle_help",
            "Advisory only — this does not delete AI outputs; you still review everything before use.",
          )}
        </span>
      </div>
    </div>
  );
}
